import React, { useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { SubscriptionEnrollmentTotalsDto, EarningsPerMonth } from '../Dtos/AppUsageDashboard/SubscriptionEnrollmentTotalsDto';
import { useCookies } from 'react-cookie';
import { DEFAULT_HEADERS, to_API_URL } from '../Services/RestService';

interface FinancialsData {
    totalUsersEnrolled: SubscriptionEnrollmentTotalsDto[];
    earningsPerMonth: EarningsPerMonth[]
}


const FinancialDashboard: React.FC<any> = () => {
    const [cookies] = useCookies();
    const [financialsData, setFinancialsData] = useState<FinancialsData>({
        earningsPerMonth: [],
        totalUsersEnrolled: []
    });

    async function initialize() {
        const result = await fetch(to_API_URL("api/v1/dashboard/financials"), {
            method: "GET",
            headers: {
                ...DEFAULT_HEADERS,
                "Authorization": "Bearer " + cookies.accessToken,
            }
        });
        if (result.ok) {
            const data: FinancialsData = await result.json();
            console.log("result", data);
            setFinancialsData(data);
        }
    }

    useEffect(() => {
        initialize();
    }, [])

    // Destructure the financialsData prop
    const { totalUsersEnrolled, earningsPerMonth } = financialsData || {};

    // Convert euro cents to euros with proper formatting
    const formatEuros = (amountInCents: number) => {
        if (amountInCents === undefined || amountInCents === null) return 'N/A';
        const euros = (amountInCents / 100).toLocaleString('de-DE', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2
        });
        return euros;
    };

    // Calculate total metrics
    const totalMetrics = useMemo(() => {
        if (!earningsPerMonth || !totalUsersEnrolled) return null;

        // Calculate total earnings across all months
        const totalEarnings = earningsPerMonth.reduce(
            (sum, month) => sum + (month.totalInEurCents || 0),
            0
        );

        // Calculate total monthly revenue
        const totalMonthlyRevenue = totalUsersEnrolled.reduce(
            (sum, sub) => sum + (sub.totalMonthlyValueEuroCents || 0),
            0
        );

        // Calculate highest earning month
        const highestEarningMonth: EarningsPerMonth = earningsPerMonth.reduce(
            (highest: EarningsPerMonth, month: EarningsPerMonth) =>
                (month.totalInEurCents || 0) > (highest.totalInEurCents || 0) ? month : highest,
            { totalInEurCents: 0, title: "", month: "" }
        );

        return {
            totalEarnings,
            totalMonthlyRevenue,
            highestEarningMonth: {
                month: highestEarningMonth.month,
                amount: highestEarningMonth.totalInEurCents
            }
        };
    }, [earningsPerMonth, totalUsersEnrolled]);

    // Format earnings data for chart - group by month and separate by title
    const formattedEarningsData = useMemo(() => {
        if (!earningsPerMonth) return [];

        // Group by month first
        const groupedByMonth = earningsPerMonth.reduce((acc, item) => {
            const existingMonth = acc.find(m => m.name === item.month);

            if (existingMonth) {
                // Add this subscription type's earnings to the existing month
                existingMonth[item.title] = item.totalInEurCents / 100; // Convert to euros
            } else {
                // Create a new month entry
                const newMonthEntry = {
                    name: item.month,
                    [item.title]: item.totalInEurCents / 100 // Convert to euros
                };
                acc.push(newMonthEntry);
            }

            return acc;
        }, []);

        return groupedByMonth;
    }, [earningsPerMonth]);

    // Get unique subscription titles for chart lines
    const subscriptionTitles = useMemo(() => {
        if (!earningsPerMonth) return [];
        return [...new Set(earningsPerMonth.map(item => item.title))];
    }, [earningsPerMonth]);

    // Format subscription data for chart
    const formattedSubscriptionData = useMemo(() => {
        if (!totalUsersEnrolled) return [];

        return totalUsersEnrolled.map(sub => ({
            name: sub.title,
            monthlyValue: sub.totalMonthlyValueEuroCents / 100, // Convert to euros for chart
            totalValue: sub.totalSoFarEurCents / 100, // Convert to euros for chart
            perUnitValue: sub.perUnitMonthlyValueEuroCents / 100 // Convert to euros for chart
        }));
    }, [totalUsersEnrolled]);

    // Get color for subscription based on index
    const getSubscriptionColor = (title, titles) => {
        const colors = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];
        const index = titles.indexOf(title);
        return colors[index % colors.length];
    };

    // Calculate month-over-month growth
    const calculateGrowth = useMemo(() => {
        if (!earningsPerMonth || earningsPerMonth.length < 2) return { percentage: 0, positive: true };

        // Group earnings by month to get totals
        const monthTotals = {};
        earningsPerMonth.forEach(item => {
            if (!monthTotals[item.month]) {
                monthTotals[item.month] = 0;
            }
            monthTotals[item.month] += (item.totalInEurCents || 0);
        });

        // Get sorted months
        const sortedMonths = Object.keys(monthTotals).sort();

        if (sortedMonths.length < 2) return { percentage: 0, positive: true };

        const lastMonth = monthTotals[sortedMonths[sortedMonths.length - 1]] || 0;
        const previousMonth = monthTotals[sortedMonths[sortedMonths.length - 2]] || 1; // Avoid division by zero

        const growthRate = ((lastMonth - previousMonth) / previousMonth) * 100;

        return {
            percentage: Math.abs(growthRate).toFixed(1),
            positive: growthRate >= 0
        };
    }, [earningsPerMonth]);

    return (
        <div className="bg-gray-50 min-h-screen p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <header className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-800">Financial Dashboard</h1>
                    <p className="text-gray-600 mt-2">Subscription revenue and monthly earnings overview</p>
                </header>

                {/* Key Metrics */}
                {totalMetrics && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center">
                                <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>
                                </div>
                                <div className="ml-4">
                                    <p className="text-gray-500 text-sm font-medium uppercase">Total Earnings</p>
                                    <p className="text-2xl font-semibold text-gray-800">{formatEuros(totalMetrics.totalEarnings)}</p>
                                </div>
                            </div>
                            <div className="mt-4">
                                <p className="text-sm text-gray-600">
                                    <span className={`font-semibold ${calculateGrowth.positive ? 'text-green-500' : 'text-red-500'}`}>
                                        {calculateGrowth.positive ? '+' : '-'}{calculateGrowth.percentage}%
                                    </span>
                                    {' '}month-over-month growth
                                </p>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center">
                                <div className="p-3 rounded-full bg-green-100 text-green-600">
                                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
                                    </svg>
                                </div>
                                <div className="ml-4">
                                    <p className="text-gray-500 text-sm font-medium uppercase">Monthly Revenue</p>
                                    <p className="text-2xl font-semibold text-gray-800">{formatEuros(totalMetrics.totalMonthlyRevenue)}</p>
                                </div>
                            </div>
                            <div className="mt-4">
                                <p className="text-sm text-gray-600">
                                    From active subscriptions
                                </p>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center">
                                <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                                    </svg>
                                </div>
                                <div className="ml-4">
                                    <p className="text-gray-500 text-sm font-medium uppercase">Best Month</p>
                                    <p className="text-2xl font-semibold text-gray-800">{totalMetrics.highestEarningMonth.month}</p>
                                </div>
                            </div>
                            <div className="mt-4">
                                <p className="text-sm text-gray-600">
                                    {formatEuros(totalMetrics.highestEarningMonth.amount)} in earnings
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Charts Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    {/* Monthly Earnings Chart */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Monthly Earnings by Subscription</h2>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={formattedEarningsData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis
                                    tickFormatter={(value) => `€${value}`}
                                    domain={[0, 'auto']}
                                />
                                <Tooltip formatter={(value) => [`€${value.toFixed(2)}`, '']} />
                                <Legend />
                                {subscriptionTitles.map((title, index) => {
                                    // Array of colors for different subscription lines
                                    const colors = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];
                                    return (
                                        <Line
                                            key={title}
                                            type="monotone"
                                            dataKey={title}
                                            name={title}
                                            stroke={colors[index % colors.length]}
                                            activeDot={{ r: 6 }}
                                            strokeWidth={2}
                                        />
                                    );
                                })}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Subscription Value Chart */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Subscription Revenue</h2>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={formattedSubscriptionData} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" tickFormatter={(value) => `€${value}`} />
                                <YAxis type="category" dataKey="name" width={120} />
                                <Tooltip formatter={(value) => [`€${value.toFixed(2)}`, 'Value']} />
                                <Legend />
                                <Bar dataKey="monthlyValue" name="Monthly Revenue" fill="#00C49F" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Subscription Details */}
                <div className="bg-white rounded-lg shadow p-6 mb-8">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">Subscription Details</h2>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription</th>
                                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Per Unit Value</th>
                                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Monthly Revenue</th>
                                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Revenue</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {totalUsersEnrolled && totalUsersEnrolled.map((subscription, index) => (
                                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{subscription.title}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatEuros(subscription.perUnitMonthlyValueEuroCents)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatEuros(subscription.totalMonthlyValueEuroCents)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatEuros(subscription.totalSoFarEurCents)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-gray-50">
                                <tr>
                                    <th scope="row" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                    <td className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"></td>
                                    <th scope="row" className="px-6 py-3 text-right text-xs font-medium text-gray-800 uppercase tracking-wider">
                                        {totalMetrics ? formatEuros(totalMetrics.totalMonthlyRevenue) : 'N/A'}
                                    </th>
                                    <th scope="row" className="px-6 py-3 text-right text-xs font-medium text-gray-800 uppercase tracking-wider">
                                        {totalMetrics ? formatEuros(totalMetrics.totalEarnings) : 'N/A'}
                                    </th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                {/* Monthly Earnings Table */}
                <div className="bg-white rounded-lg shadow p-6 mb-8">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">Monthly Earnings Breakdown</h2>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription Type</th>
                                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Earnings</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {earningsPerMonth && earningsPerMonth.map((earning, index) => (
                                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{earning.month}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <span className="px-2 py-1 text-xs font-medium rounded-full"
                                                style={{
                                                    backgroundColor: getSubscriptionColor(earning.title, subscriptionTitles),
                                                    color: 'white'
                                                }}>
                                                {earning.title}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatEuros(earning.totalInEurCents)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            {/* Calculate month totals */}
                            {earningsPerMonth && (
                                <tfoot className="bg-gray-50">
                                    {Array.from(new Set(earningsPerMonth.map(item => item.month))).map(month => {
                                        const monthTotal = earningsPerMonth
                                            .filter(item => item.month === month)
                                            .reduce((sum, item) => sum + (item.totalInEurCents || 0), 0);

                                        return (
                                            <tr key={`total-${month}`}>
                                                <th scope="row" className="px-6 py-3 text-left text-xs font-medium text-gray-800">
                                                    {month} Total
                                                </th>
                                                <td className="px-6 py-3 text-left text-xs font-medium text-gray-500"></td>
                                                <th scope="row" className="px-6 py-3 text-right text-xs font-medium text-gray-800">
                                                    {formatEuros(monthTotal)}
                                                </th>
                                            </tr>
                                        );
                                    })}
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>

                {/* Footer */}
                <footer className="bg-white rounded-lg shadow p-6">
                    <div className="flex flex-col md:flex-row justify-between items-center">
                        <div className="mb-4 md:mb-0">
                            <h2 className="text-lg font-semibold text-gray-800">Financial Summary</h2>
                            <p className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
                        </div>
                        <div className="flex space-x-4">
                            <button className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
                                Export Report
                            </button>
                            <button className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors">
                                Print
                            </button>
                        </div>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default FinancialDashboard;